import { api } from '@setplex/tria-api'
import { combine, createEvent, sample, split } from 'effector'
import { register } from '~/core/repository'
import { LEGACY_ANALYTIX_MEDIA_TYPE_TO_ANALYTIX } from '~/features/analytix'
import {
  getLibraryTabFromQuery,
  LibraryTabs,
  libraryTabsNavigationFeature,
} from '~/features/library/tabs-navigation'
import { model as guest } from '~/features/subscriber/guest'
import { selectBestTab } from '~/shared/effector'
import { aye } from '~/shared/helpers'
import { libraryFavoritesWidget } from '~/widgets/library/favorites'
import { libraryPurchasedWidget } from '~/widgets/library/purchased'
import { $libraryTranslations } from './translations'

const toContentMap = (isAnyFavorites: boolean, isAnyPurchased: boolean) =>
  ({
    [LibraryTabs.FAVORITES]: isAnyFavorites,
    [LibraryTabs.PURCHASED]: isAnyPurchased,
  }) satisfies Record<LibraryTabs, boolean>

export const initLibraryPage = createEvent<{ query: URLSearchParams }>()
export const leftLibraryPage = createEvent<{ query: URLSearchParams }>()

sample({
  clock: initLibraryPage,
  fn: (searchParams) => getLibraryTabFromQuery(searchParams.query),
  target: libraryTabsNavigationFeature.selectLibraryActiveTab,
})

//First page loading, when content isn't inited
sample({
  clock: initLibraryPage,
  source: {
    isFavoritesContentInited: libraryFavoritesWidget.$isFavoritesContentInited,
    isPurchasedContentInited: libraryPurchasedWidget.$isPurchasedContentInited,
  },
  filter: ({ isFavoritesContentInited, isPurchasedContentInited }) =>
    !isFavoritesContentInited && !isPurchasedContentInited,
  target: [
    libraryFavoritesWidget.loadFavorites,
    libraryPurchasedWidget.loadPurchased,
  ],
})

//Switch to favorite tab and update favorite content
sample({
  clock: initLibraryPage,
  source: {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    isFavoritesContentInited: libraryFavoritesWidget.$isFavoritesContentInited,
  },
  filter: ({ isFavoritesContentInited, libraryActiveTab }) =>
    isFavoritesContentInited && libraryActiveTab === LibraryTabs.FAVORITES,
  target: libraryFavoritesWidget.loadFavorites,
})

//Switch to purchased tab and update purchased content
sample({
  clock: initLibraryPage,
  source: {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    isPurchasedContentInited: libraryPurchasedWidget.$isPurchasedContentInited,
  },
  filter: ({ isPurchasedContentInited, libraryActiveTab }) =>
    isPurchasedContentInited && libraryActiveTab === LibraryTabs.PURCHASED,
  target: libraryPurchasedWidget.loadPurchased,
})

sample({
  clock: [
    libraryPurchasedWidget.$purchasedContent,
    libraryFavoritesWidget.$favoritesContent,
  ],
  source: {
    activeTab: libraryTabsNavigationFeature.$libraryActiveTab,
    isAnyFavorites: libraryFavoritesWidget.$isAnyFavorites,
    isAnyPurchased: libraryPurchasedWidget.$isAnyPurchased,
    pendingFavorites: libraryFavoritesWidget.$isAnyFavoritesFxPending,
    pendingPurchased: libraryPurchasedWidget.$isAnyPurchasedFxPending,
    favInited: libraryFavoritesWidget.$isFavoritesContentInited,
    purInited: libraryPurchasedWidget.$isPurchasedContentInited,
  },
  filter: ({
    activeTab,
    isAnyFavorites,
    isAnyPurchased,
    pendingFavorites,
    pendingPurchased,
    favInited,
    purInited,
  }) => {
    const hasContent = toContentMap(isAnyFavorites, isAnyPurchased)

    return (
      !pendingFavorites &&
      !pendingPurchased &&
      favInited &&
      purInited &&
      activeTab != null &&
      !hasContent[activeTab] &&
      (isAnyFavorites || isAnyPurchased)
    )
  },
  fn: ({ activeTab, isAnyFavorites, isAnyPurchased }) =>
    // filter above ensures activeTab is set and at least one tab has content
    selectBestTab(
      [LibraryTabs.FAVORITES, LibraryTabs.PURCHASED],
      toContentMap(isAnyFavorites, isAnyPurchased),
      activeTab!
    )!,
  target: libraryTabsNavigationFeature.selectLibraryActiveTab,
})

export const $isAnyFavoritesOrAnyPurchased = combine(
  libraryFavoritesWidget.$isAnyFavorites,
  libraryPurchasedWidget.$isAnyPurchased,
  (isAnyFavorites, isAnyPurchased) => isAnyFavorites || isAnyPurchased
)

const $isPurchasedLiveEventsAndPathname = combine(
  libraryTabsNavigationFeature.$libraryActiveTab,
  libraryPurchasedWidget.$purchasedContent,
  (libraryActiveTab, purchasedContent) =>
    libraryActiveTab === LibraryTabs.PURCHASED &&
    Boolean(purchasedContent.liveEvents?.content?.length)
)

export const $tvChannels = combine(
  {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    favoritesContent: libraryFavoritesWidget.$favoritesContent,
    purchasedContent: libraryPurchasedWidget.$purchasedContent,
  },
  ({ libraryActiveTab, favoritesContent, purchasedContent }) =>
    libraryActiveTab === LibraryTabs.FAVORITES
      ? favoritesContent.tvChannels
      : purchasedContent.tvChannels
)

export const $vods = combine(
  {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    favoritesContent: libraryFavoritesWidget.$favoritesContent,
    purchasedContent: libraryPurchasedWidget.$purchasedContent,
  },
  ({ libraryActiveTab, favoritesContent, purchasedContent }) =>
    libraryActiveTab === LibraryTabs.FAVORITES
      ? favoritesContent.vods
      : purchasedContent.vods
)

export const $tvShows = combine(
  {
    libraryActiveTab: libraryTabsNavigationFeature.$libraryActiveTab,
    favoritesContent: libraryFavoritesWidget.$favoritesContent,
    purchasedContent: libraryPurchasedWidget.$purchasedContent,
  },
  ({ libraryActiveTab, favoritesContent, purchasedContent }) =>
    libraryActiveTab === LibraryTabs.FAVORITES
      ? favoritesContent.tvShows
      : purchasedContent.tvShows
)

export const $liveEvents = combine(
  libraryTabsNavigationFeature.$libraryActiveTab,
  libraryPurchasedWidget.$purchasedContent,
  (libraryActiveTab, purchasedContent) =>
    libraryActiveTab === LibraryTabs.PURCHASED
      ? purchasedContent.liveEvents
      : []
)

export const $isFavoriteTab = combine(
  libraryTabsNavigationFeature.$libraryActiveTab,
  (libraryActiveTab) => libraryActiveTab === LibraryTabs.FAVORITES
)
export const $isPurchasedTab = combine(
  libraryTabsNavigationFeature.$libraryActiveTab,
  (libraryActiveTab) => libraryActiveTab === LibraryTabs.PURCHASED
)

const seeAllLibraryTvClick = createEvent()
const seeAllLibraryMovieClick = createEvent()
const seeAllLibraryTvShowsClick = createEvent()
const seeAllLibraryLiveEventsClick = createEvent()

split({
  clock: seeAllLibraryTvClick,
  source: libraryTabsNavigationFeature.$libraryActiveTab,
  match: {
    favorites: (libraryActiveTab) => libraryActiveTab === LibraryTabs.FAVORITES,
    purchased: (libraryActiveTab) => libraryActiveTab === LibraryTabs.PURCHASED,
  },
  cases: {
    favorites: libraryFavoritesWidget.seeAllLibraryFavoritesTvClick,
    purchased: libraryPurchasedWidget.seeAllLibraryPurchasedTvClick,
  },
})

split({
  clock: seeAllLibraryMovieClick,
  source: libraryTabsNavigationFeature.$libraryActiveTab,
  match: {
    favorites: (libraryActiveTab) => libraryActiveTab === LibraryTabs.FAVORITES,
    purchased: (libraryActiveTab) => libraryActiveTab === LibraryTabs.PURCHASED,
  },
  cases: {
    favorites: libraryFavoritesWidget.seeAllLibraryFavoritesMovieClick,
    purchased: libraryPurchasedWidget.seeAllLibraryPurchasedMovieClick,
  },
})

split({
  clock: seeAllLibraryTvShowsClick,
  source: libraryTabsNavigationFeature.$libraryActiveTab,
  match: {
    favorites: (libraryActiveTab) => libraryActiveTab === LibraryTabs.FAVORITES,
    purchased: (libraryActiveTab) => libraryActiveTab === LibraryTabs.PURCHASED,
  },
  cases: {
    favorites: libraryFavoritesWidget.seeAllLibraryFavoritesTvShowsClick,
    purchased: libraryPurchasedWidget.seeAllLibraryPurchasedTvShowsClick,
  },
})

sample({
  clock: seeAllLibraryLiveEventsClick,
  target: libraryPurchasedWidget.seeAllLibraryPurchasedLiveEventsClick,
})

/**
 * Function to get correct analytix string, what content user selects.
 */
export const $myListCategoryName = combine(
  $isFavoriteTab,
  $isPurchasedTab,
  (isFavoriteTab, isPurchasedTab) => {
    return (content: { contentType: string } | null) => {
      const contentType = content?.contentType
      const type = contentType
        ? LEGACY_ANALYTIX_MEDIA_TYPE_TO_ANALYTIX[contentType]
        : ''

      if (isFavoriteTab) return type
      if (isPurchasedTab) return `Purchased ${type}`
      return 'All'
    }
  }
)

sample({
  clock: [leftLibraryPage, api.session.signOutFx.done],
  source: {
    isPurchasedContentInited: libraryPurchasedWidget.$isPurchasedContentInited,
    isFavoritesContentInited: libraryFavoritesWidget.$isFavoritesContentInited,
  },
  filter: ({ isFavoritesContentInited, isPurchasedContentInited }) =>
    isFavoritesContentInited || isPurchasedContentInited,
  target: [
    libraryFavoritesWidget.resetFavorites,
    libraryPurchasedWidget.resetPurchased,
  ],
})
// reset store on session expiring and when guest mode
sample({
  clock: guest.$guest,
  source: {
    isPurchasedContentInited: libraryPurchasedWidget.$isPurchasedContentInited,
    isFavoritesContentInited: libraryFavoritesWidget.$isFavoritesContentInited,
  },
  filter: ({ isFavoritesContentInited, isPurchasedContentInited }, isGuest) =>
    (isFavoritesContentInited || isPurchasedContentInited) && aye(isGuest),
  target: [
    libraryFavoritesWidget.resetFavorites,
    libraryPurchasedWidget.resetPurchased,
  ],
})

libraryTabsNavigationFeature.$libraryActiveTab.reset(leftLibraryPage)

register($libraryTranslations, '$libraryTranslations')
register(libraryTabsNavigationFeature.$libraryTabNames, '$libraryTabNames')
register(libraryPurchasedWidget.$isAnyPurchased, '$isAnyPurchased')
register(libraryFavoritesWidget.$isAnyFavorites, '$isAnyFavorites')
register($isAnyFavoritesOrAnyPurchased, '$isAnyFavoritesOrAnyPurchased')
register($tvChannels, '$libraryTvChannels')
register($vods, '$libraryVods')
register($tvShows, '$libraryTvShows')
register($liveEvents, '$libraryLiveEvents')
register(seeAllLibraryTvClick, 'seeAllLibraryTvClick')
register(seeAllLibraryMovieClick, 'seeAllLibraryMovieClick')
register(seeAllLibraryTvShowsClick, 'seeAllLibraryTvShowsClick')
register(seeAllLibraryLiveEventsClick, 'seeAllLibraryLiveEventsClick')
register($isPurchasedLiveEventsAndPathname, '$isPurchasedLiveEventsAndPathname')
register(guest.$guest, '$guest')
register($isFavoriteTab, '$isFavoriteTab')
register($isPurchasedTab, '$isPurchasedTab')
register(libraryTabsNavigationFeature.$libraryActiveTab, '$libraryActiveTab')
register($myListCategoryName, '|features/analytix/myListCategoryName')
